/**
 * Runtime validation for Input and Response contracts.
 *
 * Inputs (frontend → backend payloads):
 *   registerContract(name, fields)  + validate(name, data)
 *
 * Responses (backend → frontend envelopes):
 *   registerResponseContract(name, fields)  + validateResponse(name, response)
 *
 * Both share the same FieldSchema type and recursive validateFields() engine
 * but have separate registries — input/response names can collide without
 * ambiguity.
 *
 * Supports:
 * - Flat fields: string, number, boolean, array
 * - Nested objects: type 'object' with `fields` sub-schema
 * - Arrays of objects: type 'array' with `items` sub-schema
 *
 * See orchestrator skill `contract-system` for the full pipeline (PHP source-
 * of-truth → Spatie TS generator → this registry → httpService.responseContract
 * runtime guard). This file is the only hand-written file in src/contracts/;
 * everything else is auto-generated and the PreToolUse hook blocks manual edits.
 */
import type { ApiResponse } from '../services/httpService'
import type { InputContractRegistry } from './registry'

export type FieldType = 'string' | 'number' | 'boolean' | 'object' | 'array'

export interface FieldSchema {
  type: FieldType
  required: boolean
  fields?: Record<string, FieldSchema>  // nested schema for type: 'object'
  items?: Record<string, FieldSchema>   // item schema for type: 'array' of objects
}

const registry = new Map<string, Record<string, FieldSchema>>()

export function registerContract(name: string, fields: Record<string, FieldSchema>): void {
  registry.set(name, fields)
}

/**
 * Read back the registered field schema for an Input contract.
 *
 * Mirror of `getResponseSchema` for the input registry. Used by the
 * registry-data.json conformance test to deep-compare the JSON projection the
 * integration tier loads at runtime against what registry-runtime.ts actually
 * registers — divergence fails loud instead of silently validating against a
 * stale schema.
 */
export function getInputSchema(
  name: string
): Record<string, FieldSchema> | undefined {
  return registry.get(name)
}

/**
 * Validate fields against a schema, collecting errors with a key prefix.
 */
function validateFields(
  data: Record<string, unknown>,
  fields: Record<string, FieldSchema>,
  prefix: string = ''
): string[] {
  const errors: string[] = []

  for (const [field, schema] of Object.entries(fields)) {
    const fullKey = prefix ? `${prefix}.${field}` : field
    const value = data[field]

    // Empty string is a valid `string` value — only treat null/undefined as
    // missing for string-typed fields. For non-string types (number, boolean,
    // object, array), `''` is still treated as missing because it's an
    // out-of-band placeholder rather than a real value.
    const isStringType = schema.type === 'string'
    const treatAsMissing = value === null || value === undefined || (!isStringType && value === '')
    if (schema.required && treatAsMissing) {
      errors.push(`"${fullKey}" is required`)
      continue
    }

    if (!treatAsMissing) {
      const actualType = Array.isArray(value) ? 'array' : typeof value

      if (actualType !== schema.type) {
        // Coerce numeric strings to numbers (HTML forms and API responses return strings)
        if (schema.type === 'number' && actualType === 'string' && value !== '' && !isNaN(Number(value))) {
          (data as Record<string, unknown>)[field] = Number(value)
        } else if (schema.type === 'object' && Array.isArray(value) && value.length === 0) {
          // PHP serializer reality: json_encode cannot distinguish an EMPTY
          // map from an empty list — both come over the wire as `[]`. An
          // object-typed field (a map, e.g. `@var array<string, X>`) is
          // therefore legitimately `[]` when empty. Only the empty array is
          // accepted; a non-empty array here is still real drift.
        } else {
          errors.push(`"${fullKey}" must be ${schema.type}, got ${actualType}`)
        }
      }

      // Validate nested object fields
      if (schema.type === 'object' && schema.fields && typeof value === 'object' && !Array.isArray(value)) {
        errors.push(...validateFields(value as Record<string, unknown>, schema.fields, fullKey))
      }

      // Validate array items
      if (schema.type === 'array' && schema.items && Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          const item = value[i]
          if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
            errors.push(...validateFields(item as Record<string, unknown>, schema.items, `${fullKey}[${i}]`))
          }
        }
      }
    }
  }

  return errors
}

/**
 * Validate a payload against a registered Input contract.
 *
 * The contract name is bound to the auto-generated `InputContractRegistry`
 * keys — typo'd contract names fail to compile. The `data` parameter is
 * deliberately loose (`object`) rather than `InputContractRegistry[K]`:
 * many callers assemble payloads as `Record<string, unknown>` or pass
 * a typed local that's a structural superset of the contract, and tightening
 * to the exact interface would surface dozens of TS errors that are better
 * caught at the per-composable rollout (see `composable-input-not-any` rule).
 * Runtime validation against the schema is the shape fence; this signature
 * is the contract-name fence.
 */
export function validate<K extends keyof InputContractRegistry>(
  contractName: K,
  data: object
): object {
  const fields = registry.get(contractName)
  if (!fields) {
    throw new Error(`Contract "${contractName}" is not registered`)
  }

  // The internal indexer expects Record<string, unknown>; the cast is safe
  // because validateFields only reads keys declared in the schema.
  const errors = validateFields(data as unknown as Record<string, unknown>, fields)

  if (errors.length > 0) {
    throw new Error(`${contractName} validation failed:\n  - ${errors.join('\n  - ')}`)
  }

  return data
}

// ============================================================================
// Response contracts (backend → frontend envelope)
// ============================================================================

const responseRegistry = new Map<string, Record<string, FieldSchema>>()

/**
 * Register the schema for a Response contract's `dados` payload.
 * The `dados` shape lives at envelope-level position `response.dados`.
 *
 * For object-shaped dados, pass the field schema directly:
 *   registerResponseContract('Cliente_BuscarResponse', {
 *     cliente_id: { type: 'number', required: true },
 *     cliente_nome: { type: 'string', required: true },
 *   })
 *
 * For primitive dados (a single id, count, etc.), pass an empty schema —
 * the envelope is checked but `dados` shape verification is skipped.
 *
 * For top-level array dados (list-of-Row, where `getDados()` returns the array
 * directly), register a single `_root` key whose schema is `{ type: 'array',
 * required: true, items: {...} }`. `validateResponse` treats `_root` as "the
 * dados IS this array" and validates each element against `items`.
 *
 * When the PHP contract declares its payload property nullable (`?Row $dados`),
 * the generator additionally emits a reserved `_nullable` key — a metadata
 * marker (never a real dados field) telling `validateResponse` that
 * `dados: null` is a valid success payload for this contract. The contract
 * generator emits this automatically when the returned property is annotated
 * `@var XRow[]`.
 */
export function registerResponseContract(name: string, fields: Record<string, FieldSchema>): void {
  responseRegistry.set(name, fields)
}

/**
 * Read back the registered field schema for a Response contract.
 *
 * Returns the exact `Record<string, FieldSchema>` that was registered via
 * `registerResponseContract` (including the `_root` array entry for
 * list-of-Row contracts), or `undefined` if no contract is registered under
 * that name. The integration-tier fixture generator uses this to derive
 * contract-valid MSW response bodies directly from the registered schema, so
 * fixtures cannot drift from the contract.
 */
export function getResponseSchema(
  name: string
): Record<string, FieldSchema> | undefined {
  return responseRegistry.get(name)
}

/**
 * Validate a backend JSON response against a registered Response contract.
 *
 * Asserts the canonical envelope shape `{ success, dados, error }`. On
 * success responses, validates the `dados` payload against the registered
 * schema. On failure responses (`success === false`), only the envelope is
 * checked — `dados` is expected to be null.
 *
 * Throws on any mismatch. The error message lists each violation by field path.
 *
 * Returns the response cast to `ApiResponse<T>` for the caller's convenience.
 */
export function validateResponse<T = unknown>(
  contractName: string,
  response: unknown
): ApiResponse<T> {
  const schema = responseRegistry.get(contractName)
  if (!schema) {
    throw new Error(`Response contract "${contractName}" is not registered`)
  }

  if (typeof response !== 'object' || response === null || Array.isArray(response)) {
    throw new Error(`${contractName}: response is not an object`)
  }

  const env = response as Record<string, unknown>

  const errors: string[] = []

  // Envelope keys
  if (typeof env.success !== 'boolean') {
    errors.push('"success" must be boolean')
  }
  if (env.error !== null && env.error !== undefined && typeof env.error !== 'string') {
    errors.push('"error" must be string or null')
  }

  // Reserved schema keys (never real dados fields):
  //   _root     — "dados IS this array" (list-of-Row contracts);
  //   _nullable — the PHP contract declares its payload property nullable
  //               (`?Row $dados`), so `dados: null` is a valid success
  //               payload. Emitted by the generator from the PHP source of
  //               truth; without it the guard rejected documented null
  //               payloads (probe-conformance findings, 2026-08-09).
  const rootArray = schema._root
  const dadosNullable = schema._nullable !== undefined
  const { _root: _omitRoot, _nullable: _omitNullable, ...fieldSchema } = schema

  // Top-level array dados (list-of-Row): the schema is a single `_root` array
  // entry and `dados` IS the array. Validate each element against `_root.items`.
  if (env.success === true && rootArray && rootArray.type === 'array') {
    if (env.dados === null || env.dados === undefined) {
      if (!dadosNullable) {
        errors.push('"dados" must be present on success response')
      }
    } else if (!Array.isArray(env.dados)) {
      errors.push('"dados" must be an array when contract declares a list-of-Row shape')
    } else if (rootArray.items) {
      for (let i = 0; i < env.dados.length; i++) {
        const item = env.dados[i]
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          errors.push(...validateFields(item as Record<string, unknown>, rootArray.items, `dados[${i}]`))
        } else {
          errors.push(`"dados[${i}]" must be an object`)
        }
      }
    }
  } else if (env.success === true && Object.keys(fieldSchema).length > 0) {
    if (env.dados === null || env.dados === undefined) {
      if (!dadosNullable) {
        errors.push('"dados" must be present on success response')
      }
    } else if (typeof env.dados !== 'object' || Array.isArray(env.dados)) {
      // Primitive / array dados: schema must be empty (caller registered an empty
      // schema to opt out of dados-shape checking). Already filtered out above.
      errors.push('"dados" must be an object when contract declares fields')
    } else {
      errors.push(...validateFields(env.dados as Record<string, unknown>, fieldSchema, 'dados'))
    }
  }

  if (errors.length > 0) {
    throw new Error(`${contractName} response validation failed:\n  - ${errors.join('\n  - ')}`)
  }

  return env as unknown as ApiResponse<T>
}
